/**
 * VoiceConfigPanel.jsx
 * ====================
 * Per-track voice prompt configuration panel. Shows each track segment with
 * on/off toggles for Entry Speed, Min Speed, and Exit Speed voice prompts.
 * Saves config as voiceconfig_{trackId}.json in user's tracks folder.
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { parseSegments } from '../utils/trackRenderer';
import { authHeaders } from '../utils/authService';
import LoadingProgress from './LoadingProgress';

const PROMPT_TYPES = [
  { key: 'entrySpeed', label: 'Entry Speed' },
  { key: 'minSpeed', label: 'Min Speed' },
  { key: 'exitSpeed', label: 'Exit Speed' },
];

const styles = {
  container: {
    padding: '20px 0',
    maxWidth: 800,
  },
  headerRow: {
    marginBottom: 12,
  },
  title: {
    fontFamily: 'var(--font-heading)',
    fontSize: 14,
    fontWeight: 700,
    color: 'var(--color-secondary)',
    letterSpacing: 1.8,
    textTransform: 'uppercase',
    marginBottom: 0,
  },
  allOffButton: {
    padding: '9px 14px',
    background: 'transparent',
    border: '1px solid var(--color-border)',
    borderRadius: 8,
    color: 'var(--color-text)',
    fontFamily: 'var(--font-heading)',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    cursor: 'pointer',
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
  },
  th: {
    padding: '8px 14px',
    fontFamily: 'var(--font-heading)',
    fontSize: 11,
    fontWeight: 700,
    color: 'var(--color-text-dim)',
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    textAlign: 'center',
    borderBottom: '1px solid var(--color-border)',
  },
  thLabel: {
    padding: '8px 14px',
    fontFamily: 'var(--font-heading)',
    fontSize: 11,
    fontWeight: 700,
    color: 'var(--color-text-dim)',
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    textAlign: 'left',
    borderBottom: '1px solid var(--color-border)',
  },
  td: {
    padding: '10px 14px',
    textAlign: 'center',
    borderBottom: '1px solid rgba(138,180,200,0.1)',
  },
  tdLabel: {
    padding: '10px 14px',
    fontFamily: 'var(--font-heading)',
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--color-text)',
    letterSpacing: 0.5,
  },
  toggle: {
    width: 40,
    height: 22,
    borderRadius: 11,
    border: 'none',
    cursor: 'pointer',
    position: 'relative',
    transition: 'background 0.2s',
    display: 'inline-block',
    verticalAlign: 'middle',
  },
  toggleKnob: {
    width: 16,
    height: 16,
    borderRadius: '50%',
    background: '#fff',
    position: 'absolute',
    top: 3,
    transition: 'left 0.2s',
  },
  statusText: {
    fontFamily: 'var(--font-body)',
    fontSize: 12,
    color: 'var(--color-text-dim)',
    marginTop: 12,
  },
  noSegments: {
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    color: 'var(--color-text-dim)',
    lineHeight: 1.6,
  },
};

export default function VoiceConfigPanel({ trackId, trackCoordinates, user }) {
  const [segments, setSegments] = useState({});
  const [loading, setLoading] = useState(true);
  const [savedSessionId, setSavedSessionId] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null); // 'saving' | 'saved' | 'error'
  const loadedRef = useRef(false);
  const segmentsRef = useRef({});

  const parsedSegments = useMemo(() => {
    if (!trackCoordinates?.length) return [];
    return parseSegments(trackCoordinates);
  }, [trackCoordinates]);

  useEffect(() => {
    if (!trackId) return;
    loadedRef.current = false;
    setLoading(true);
    fetch(`/api/voice-config/${trackId}`, { headers: authHeaders() })
      .then(res => res.ok ? res.json() : { segments: {} })
      .then(data => {
        setSegments(data.segments || {});
        setSavedSessionId(data.sessionId || null);
        loadedRef.current = true;
      })
      .catch(() => { setSegments({}); loadedRef.current = true; })
      .finally(() => setLoading(false));
  }, [trackId]);

  useEffect(() => {
    segmentsRef.current = segments;
  }, [segments]);

  const saveConfig = useCallback(async (newSegments) => {
    setSaveStatus('saving');
    try {
      // Enrich segments with GPS positions and labels from track coordinates
      const enriched = {};
      for (const [idx, config] of Object.entries(newSegments)) {
        const parsed = parsedSegments[Number(idx)];
        const startCoord = parsed ? trackCoordinates[parsed.startIdx] : null;
        const stopCoord = parsed ? trackCoordinates[parsed.endIdx] : null;
        enriched[idx] = {
          ...config,
          label: parsed?.label || null,
          start: startCoord ? { lat: startCoord.lat, lng: startCoord.lng } : null,
          stop: stopCoord ? { lat: stopCoord.lat, lng: stopCoord.lng } : null,
        };
      }
      const res = await fetch(`/api/voice-config/${trackId}`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ segments: enriched }),
      });
      if (res.ok) {
        const data = await res.json();
        setSavedSessionId(data.sessionId);
        setSaveStatus('saved');
      } else {
        setSaveStatus('error');
      }
    } catch (err) {
      console.error('Failed to save voice config:', err);
      setSaveStatus('error');
    }
  }, [trackId, parsedSegments, trackCoordinates]);

  const updateSegments = useCallback((buildUpdatedSegments) => {
    const updated = buildUpdatedSegments(segmentsRef.current);
    segmentsRef.current = updated;
    setSegments(updated);
    saveConfig(updated);
  }, [saveConfig]);

  const handleToggle = useCallback((segIndex, promptKey) => {
    updateSegments((currentSegments) => {
      const seg = currentSegments[segIndex] || { entrySpeed: false, minSpeed: false, exitSpeed: false };
      return {
        ...currentSegments,
        [segIndex]: {
          ...seg,
          [promptKey]: !seg[promptKey],
        },
      };
    });
  }, [updateSegments]);

  const handleAllOff = useCallback(() => {
    updateSegments((currentSegments) => {
      const updated = { ...currentSegments };

      parsedSegments.forEach((_, idx) => {
        updated[idx] = {
          ...(currentSegments[idx] || {}),
          entrySpeed: false,
          minSpeed: false,
          exitSpeed: false,
        };
      });

      return updated;
    });
  }, [parsedSegments, updateSegments]);

  if (loading) {
    return <LoadingProgress label="Loading voice configuration" compact />;
  }

  if (parsedSegments.length === 0) {
    return (
      <div style={styles.noSegments}>
        No segments defined for this track. Segments are needed to configure voice prompts.
      </div>
    );
  }

  return (
    <div data-doc-id="voice-config-panel" style={styles.container}>
      <div style={styles.headerRow}>
        <div style={styles.title}>Voice Prompts by Segment</div>
      </div>
      <button
        type="button"
        style={{ ...styles.allOffButton, marginBottom: 12 }}
        onClick={handleAllOff}
        data-doc-id="voice-config-all-off"
      >
        Turn All Voice Prompts Off
      </button>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.thLabel}>Segment</th>
            {PROMPT_TYPES.map(p => (
              <th key={p.key} style={styles.th}>{p.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {parsedSegments.map((seg, idx) => {
            const config = segments[idx] || {};
            return (
              <tr key={idx}>
                <td style={styles.tdLabel}>{seg.label || `Segment ${idx + 1}`}</td>
                {PROMPT_TYPES.map(p => {
                  const enabled = !!config[p.key];
                  return (
                    <td key={p.key} style={styles.td}>
                      <button
                        type="button"
                        onClick={() => handleToggle(idx, p.key)}
                        style={{
                          ...styles.toggle,
                          background: enabled ? '#27ae60' : 'rgba(138,180,200,0.2)',
                        }}
                        aria-label={`${p.label} for ${seg.label || `Segment ${idx + 1}`}: ${enabled ? 'on' : 'off'}`}
                      >
                        <span style={{ ...styles.toggleKnob, left: enabled ? 21 : 3 }} />
                      </button>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      {saveStatus && (
        <div style={styles.statusText}>
          {saveStatus === 'saving' && 'Saving...'}
          {saveStatus === 'saved' && savedSessionId && `Saved \u00b7 sync ID: ${savedSessionId}`}
          {saveStatus === 'error' && 'Failed to save — try again'}
        </div>
      )}
    </div>
  );
}
